import {
  FILTER_TYPE,
  API_INFINITY_VALUE,
  filters,
} from '../../../../../constants';
import { dots } from '../../../../../functions';
import type { CustomCompanyFilter } from './types';

const titleFormatter = ([_, __, min, max]: (number | string)[]) =>
  `is between ${min} and ${max === API_INFINITY_VALUE ? '∞' : max}`;

export const generateFuturePerformanceFilters = (): CustomCompanyFilter[] => [
  //Forecast Earnings Growth
  {
    id: filters.futureThreeYearGrowth,
    label: 'Forecast Earnings Growth',
    subLabel: '(Annual growth in Earnings over the next 3 years)',
    labels: {
      ...dots(130, { start: -30, step: 5 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      25: '25%',
      50: '50%',
      75: '75%',
      105: '∞',
    },
    defaultValue: `["${filters.futureThreeYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.futureThreeYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 105;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 105,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 100,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 100
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  //Forecast Revenue Growth
  {
    id: filters.revenue_growth_annual,
    label: 'Forecast Revenue Growth',
    subLabel: '(Annual growth in Revenue over the next 3 years)',
    labels: {
      ...dots(230, { start: -30, step: 10 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      50: '50%',
      100: '100%',
      150: '150%',
      205: '∞',
    },
    defaultValue: `["${filters.revenue_growth_annual}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.revenue_growth_annual}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 205;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 205,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 200,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 200
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // 1 year earnings
  {
    id: filters.futureOneYearGrowth,
    label: '1 Year Earnings Growth',
    subLabel: '(Forecast Earnings growth in 1 year)',
    labels: {
      ...dots(60, { start: -30, step: 15 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      30: '30%',
      35: '∞',
    },
    defaultValue: `["${filters.futureOneYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.futureOneYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 35;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 35,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 30,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 30
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // 1 year revenue
  {
    id: filters.revenue_growth_1y,
    label: '1 Year Revenue Growth',
    subLabel: '(Forecast Revenue growth in 1 year)',
    labels: {
      ...dots(130, { start: -30, step: 5 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      25: '25%',
      50: '50%',
      75: '75%',
      105: '∞',
    },
    defaultValue: `["${filters.revenue_growth_1y}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.revenue_growth_1y}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 105;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 105,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 100,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 100
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // 3 year ROE
  {
    defaultValue: `["${filters.futureThreeYearRoe}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.futureThreeYearRoe}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.futureThreeYearRoe,
    label: '3 Year ROE Estimate',
    subLabel: '(Return on Equity)',
    labels: { ...dots(50, { step: 5 }), 0: '0%', 50: '50%', 55: '∞' },
    min: 0,
    max: 55,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // (Forward Price to Earnings)
  {
    defaultValue: `["${filters.future_forward_pe1y}", "between", 0, 10]`,
    ruleToValue: ([_, __, ___, value]) => value,
    rule: (value) => {
      if (typeof value === 'number') {
        return `["${filters.future_forward_pe1y}", "between", 0, ${
          value === 55 ? API_INFINITY_VALUE : value
        }]`;
      }
      return `["${filters.future_forward_pe1y}", "between", 0, 10]`;
    },
    id: filters.future_forward_pe1y,
    label: 'Forward PE Ratio',
    subLabel: '(Forecast 1 year Price to Earnings Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range) => {
      return range > 50 ? 55 : range;
    },
    titleFormatter,
  },
  // Forward Price to Sales Ratio
  {
    defaultValue: `["${filters.future_forward_ps1y}", "between", 5, 20]`,
    ruleToValue: ([_, __, ___, value]) => value,
    rule: (value) => {
      if (typeof value === 'number') {
        return `["${filters.future_forward_ps1y}", "between", 0, ${
          value === 105 ? API_INFINITY_VALUE : value
        }]`;
      }
      return `["${filters.future_forward_ps1y}", "between", 5, 20]`;
    },
    id: filters.future_forward_ps1y,
    label: 'Forward PS Ratio',
    subLabel: '(Forecast 1 year Price to Sales Ratio)',
    labels: {
      ...dots(100, { step: 5 }),
      0: '0',
      20: '20',
      50: '50',
      100: '100',
      105: '∞',
    },
    max: 105,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range) => {
      return range > 50 ? 105 : range;
    },
    titleFormatter,
  },
  // (Enterprise Value to EBITDA Ratio)
  {
    defaultValue: `["${filters.future_forward_ev_to_ebitda1y}", "between", 0, 10]`,
    ruleToValue: ([_, __, ___, value]) => value,
    rule: (value) => {
      if (typeof value === 'number') {
        return `["${filters.future_forward_ev_to_ebitda1y}", "between", 0, ${
          value === 55 ? API_INFINITY_VALUE : value
        }]`;
      }
      return `["${filters.future_forward_ev_to_ebitda1y}", "between", 0, 10]`;
    },
    id: filters.future_forward_ev_to_ebitda1y,
    label: 'Forward EV to Ebitda',
    subLabel: '(Forecast 1 year Enterprise Value to EBITDA Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range) => {
      return range > 50 ? 55 : range;
    },
    titleFormatter,
  },
  // (Enterprise Value to Sales Ratio)
  {
    defaultValue: `["${filters.future_forward_ev_to_sales1y}", "between", 0, 10]`,
    ruleToValue: ([_, __, ___, value]) => value,
    rule: (value) => {
      if (typeof value === 'number') {
        return `["${filters.future_forward_ev_to_sales1y}", "between", 0, ${
          value === 55 ? API_INFINITY_VALUE : value
        }]`;
      }
      return `["${filters.future_forward_ev_to_sales1y}", "between", 0, 10]`;
    },
    id: filters.future_forward_ev_to_sales1y,
    label: 'Forward EV to Sales',
    subLabel: '(Forecast 1 year Enterprise Value to Sales Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range) => {
      return range > 50 ? 55 : range;
    },
    titleFormatter,
  },
];
